import { Component, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Params, Router } from '@angular/router';
import { EdtellFormControl, EdtellFormGroup, EdtellHeaderService, NavigationService, NotificationService, SrsCheckboxConfig, srsFormatDateTime, SrsFormFieldConfig, SrsFormState, SrsTextAreaComponent, SrsTextAreaConfig, TextPopUpConfig } from "@edtell-common";
import { first } from "rxjs/operators";
import { falsey } from '../../../edtell-common/edtell-utilities/functions/falsey.function';
import { RegistrationDetail } from '../../interfaces/registration-detail.interface';
import { RegistrationPayment } from '../../interfaces/registration-payment.interface';
import { RegistrationProfileCustomerData } from '../../interfaces/registration-profile-customer-data.interface';
import { RegistrationProfileData } from "../../interfaces/registration-profile.interface";
import { Registration } from '../../interfaces/registration.interface';
import { StudentHistoryCourse } from '../../interfaces/student-history.interface';
import { RegistrationHeaderService } from '../../services/registration-header.service';
import { RegistrationProfileService } from "../../services/registration-profile.service";

@Component({
  selector: "app-registration-header",
  templateUrl: "./registration-header.component.html",
  styleUrls: ["./registration-header.component.scss"],
})
export class RegistrationHeaderComponent implements OnInit {
  @ViewChild("iepTextArea")
  iepTextAreaComponent: SrsTextAreaComponent;

  dataLoaded: boolean = false;
  historyLoaded: boolean = false;
  formState: SrsFormState = SrsFormState.WRITE;

  details: RegistrationDetail[];
  historyDetails: StudentHistoryCourse[];
  payments: RegistrationPayment[];

  registrationProfileData: RegistrationProfileData;
  customerData: RegistrationProfileCustomerData;

  studentName: string;
  routeParams: Params;

  dialogRef: MatDialogRef<any>;
  currentRegId: number;

  popUpConfig: TextPopUpConfig = {
    align: "right",
  };

  ncaaPopUpConfig: TextPopUpConfig = {
    align: "right",
    closePopupCallback: () => {
      this.ncaaFormGroup.reset();
    },
  };

  iepPopUpConfig: TextPopUpConfig = {
    align: "right",
    closePopupCallback: () => {
      this.iepFormGroup.reset();
    },
    openPopupCallback: () => {
      setTimeout(() => {
        this.iepTextAreaComponent.resizeHeightToContent();
      });
    },
  };

  fncPopUpConfig: TextPopUpConfig = {
    align: "right",
    closePopupCallback: () => {
      this.fncFormGroup.reset();
    },
  };

  ncaaFormGroup: EdtellFormGroup;
  iepFormGroup: EdtellFormGroup;
  fncFormGroup: EdtellFormGroup;

  originalNcaaValue: Registration;
  originalIepValue: Registration;
  originalFncValue: Registration;

  ncaaFormFieldConfig: SrsFormFieldConfig;
  ncaaProspectCheckboxConfig: SrsCheckboxConfig;

  iepTextAreaConfig: SrsTextAreaConfig;
  iepCheckboxConfig: SrsCheckboxConfig;

  fncTextAreaConfig: SrsTextAreaConfig;
  fncCheckboxConfig: SrsCheckboxConfig;

  constructor(
    private registrationProfileService: RegistrationProfileService,
    private registrationHeaderService: RegistrationHeaderService,
    private edtellHeaderService: EdtellHeaderService,
    private notificationService: NotificationService,
    // Needed so that access logs are made when accessing registration routes
    private navigationService: NavigationService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        if(falsey(this.registrationProfileData)){
          await this.refreshHeaderData();
        }
      }
    });
    await this.refreshHeaderData();
    await this.initFormGroups();
    this.initFormTemplates();
  }

  async initFormGroups() {
    this.formState = await this.registrationProfileService
      .isRegistrationContactActive(this.registrationProfileData.registration.id) 
      ? SrsFormState.WRITE
      : SrsFormState.READ;
    const registration = this.registrationProfileData.registration;
    this.ncaaFormGroup = new EdtellFormGroup(this.formState, {
      ncaaProspect: new EdtellFormControl(registration.ncaaProspect),
      ncaaStudentNumber: new EdtellFormControl(registration.ncaaStudentNumber)
    })
    this.iepFormGroup = new EdtellFormGroup(this.formState, {
      iep: new EdtellFormControl(registration.iep),
      iepText: new EdtellFormControl(registration.iepText, [Validators.maxLength(500)])
    })
    this.fncFormGroup = new EdtellFormGroup(this.formState, {
      fnc: new EdtellFormControl(registration.fnc),
      fncInfo: new EdtellFormControl(registration.fncInfo, [
        Validators.maxLength(500),
      ]),
    });

    this.originalNcaaValue = this.ncaaFormGroup.value;
    this.originalIepValue = this.iepFormGroup.value;
    this.originalFncValue = this.fncFormGroup.value;
  }

  initFormTemplates() {
    this.ncaaFormFieldConfig = {
      key: "ncaaStudentNumber",
      size: 12,
      title: "NCAA Student Number",
      group: this.ncaaFormGroup,
      maxCharacters: 10,
      settings: {
        // numberInput: true,
        hideMaxCharacterHint: true,
      },
      disabled: () => {
        return !this.ncaaFormGroup.get("ncaaProspect").value;
      },
    };

    this.ncaaProspectCheckboxConfig = {
      key: "ncaaProspect",
      size: 12,
      title: 'Prospective NCAA Athlete',
      disabled: () => this.formState == SrsFormState.READ,
      group: this.ncaaFormGroup,
      settings: {
        padding: 0,
      },
    };

    this.iepTextAreaConfig = {
      key: "iepText",
      size: 12,
      title: "IEP Text",
      group: this.iepFormGroup,
      autoHeightToContent: true,
      disabled: () => {
        return !this.iepFormGroup.get("iep").value;
      },
      maxCharacters: 500,
    };

    this.iepCheckboxConfig = {
      key: "iep",
      size: 12,
      title: "IEP",
      group: this.iepFormGroup,
      disabled: () => this.formState == SrsFormState.READ,
      settings: {
        padding: 0,
      },
    };

    this.fncTextAreaConfig = {
      key: "fncInfo",
      size: 12,
      title: "FNC Info",
      group: this.fncFormGroup,
      disabled: () => {
        return !this.fncFormGroup.get("fnc").value;
      },
      maxCharacters: 500,
    };

    this.fncCheckboxConfig = {
      key: "fnc",
      size: 12,
      title: "FNC",
      group: this.fncFormGroup,
      disabled: () => this.formState == SrsFormState.READ,
      settings: {
        padding: 0,
      },
    };
  }

  async refreshHeaderData() {
    const routeParams = this.edtellHeaderService.getRouteParams();
    const registrationId: string = routeParams.id;

    const regProfilePromise: Promise<RegistrationProfileData> = this.registrationProfileService.getRegistrationProfile(registrationId.toString()).pipe(first()).toPromise();
    const customerDataPromise: Promise<RegistrationProfileCustomerData> = this.registrationProfileService.getCustomerData(registrationId.toString()).pipe(first()).toPromise();
    const promiseResults: (RegistrationProfileData | RegistrationProfileCustomerData)[] = await Promise.all([regProfilePromise, customerDataPromise]);

    this.registrationProfileData = promiseResults[0] as RegistrationProfileData;
    this.customerData = promiseResults[1] as RegistrationProfileCustomerData;

    this.currentRegId = this.registrationProfileData.registration.id;

    const firstName = this.registrationProfileData.registration.firstName;
    const lastName = this.registrationProfileData.registration.lastName;
    const email = this.registrationProfileData.registration.email;

    this.studentName = `${firstName} ${lastName}`;
    this.dataLoaded = true;
    this.historyDetails = await this.registrationProfileService
      .getStudentHistory(firstName, lastName, email, +registrationId)
      .pipe(first())
      .toPromise();
    this.historyLoaded = true;
  }

  async submitNcaaInfo() {
    if (this.ncaaFormGroup.isOriginal) {
      return;
    }

    if (falsey(this.ncaaFormGroup.get("ncaaProspect").value)) {
      this.ncaaFormGroup.get("ncaaStudentNumber").setValue(null);
    }

    const registration: Registration = {
      ...this.registrationProfileData.registration,
      ...this.ncaaFormGroup.getRawValue(),
    };

    const resp = await this.registrationProfileService
      .updateNcaaInfo(registration)
      .pipe(first())
      .toPromise();
    let message;
    if (resp) {
      message = "NCAA info updated.";
      this.ncaaFormGroup.markAsOriginal();

      this.registrationProfileData.registration.ncaaProspect =
        this.ncaaFormGroup.get("ncaaProspect").value;
      this.registrationProfileData.registration.ncaaStudentNumber =
        this.ncaaFormGroup.get("ncaaStudentNumber").value;

      this.updateModified(resp);

      await this.registrationProfileService
        .updateRegistrationProfile(this.registrationProfileData)
        .pipe(first())
        .toPromise();

      this.originalNcaaValue = this.ncaaFormGroup.getRawValue();
    } else {
      message = "Something went wrong.";
    }
    this.notificationService.sendNotification({ message: message });
  }

  async submitIepInfo() {
    if (this.iepFormGroup.isOriginal) {
      return;
    }

    if (falsey(this.iepFormGroup.get("iep").value)) {
      this.iepFormGroup.get("iepText").setValue(null);
    }

    const registration: Registration = {
      ...this.registrationProfileData.registration,
      ...this.iepFormGroup.getRawValue(),
    };

    const resp = await this.registrationProfileService
      .updateIepInfo(registration)
      .pipe(first())
      .toPromise();
    let message;
    if (resp) {
      message = "IEP info updated.";
      this.iepFormGroup.markAsOriginal();
      this.registrationProfileData.registration.iep =
        this.iepFormGroup.get("iep").value;
      this.registrationProfileData.registration.iepText =
        this.iepFormGroup.get("iepText").value;

      this.updateModified(resp);
      await this.registrationProfileService
        .updateRegistrationProfile(this.registrationProfileData)
        .pipe(first())
        .toPromise();

      this.originalIepValue = this.iepFormGroup.getRawValue();
    } else {
      message = "Something went wrong.";
    }
    this.notificationService.sendNotification({ message: message });
  }

  async submitFncInfo() {
    if (this.fncFormGroup.isOriginal) {
      return;
    }

    if (falsey(this.fncFormGroup.get("fnc").value)) {
      this.fncFormGroup.get("fncInfo").setValue(null);
    }

    const registration: Registration = {
      ...this.registrationProfileData.registration,
      ...this.fncFormGroup.getRawValue(),
    };

    const resp = await this.registrationProfileService
      .updateFncInfo(registration)
      .pipe(first())
      .toPromise();
    let message;
    if (resp) {
      message = "FNC info updated.";
      this.fncFormGroup.markAsOriginal();
      this.updateModified(resp);
      this.registrationProfileData.registration.fnc =
        this.fncFormGroup.get("fnc").value;
      this.registrationProfileData.registration.fncInfo =
        this.fncFormGroup.get("fncInfo").value;
      this.originalFncValue = this.fncFormGroup.getRawValue();
    } else {
      message = "Something went wrong.";
    }
    this.notificationService.sendNotification({ message: message });
  }

  updateModified(resp: Registration) {
    this.registrationProfileData.registration.dateModified = resp.dateModified;
    this.registrationProfileData.registration.modifyUser = resp.modifyUser;
  }

  getRegTotal() {
    return this.registrationProfileService
      .getDetails()
      .reduce(
        (acc, curr) =>
          acc + this.registrationProfileService.getCourseRegTotal(curr),
        0,
      );
  }

  getRevenueInHand() {
    return this.registrationHeaderService.getRevenueInHand();
  }

  getRevenueInHandTax() {
    return this.registrationHeaderService.getRevenueInHandTax();
  }

  getParentPaymentPending() {
    return this.registrationHeaderService.getParentPaymentPending();
  }

  getSchoolPaymentPending() {
    return this.registrationHeaderService.getSchoolPaymentPending(
      this.registrationProfileData.paymentCodeInfo,
    );
  }

  getPaymentOwed() {
    return this.registrationHeaderService.getPaymentOwed();
  }

  getUnallocated() {
    return this.registrationHeaderService.getUnallocated();
  }

  get dateModified() {
    return srsFormatDateTime(
      this.registrationProfileData.registration.dateModified,
    );
  }

  get modifiedBy() {
    return this.registrationProfileData.registration.modifyUser;
  }

  formatDate(date) {
    return srsFormatDateTime(date);
  }

  get ncaaNoData() {
    return falsey(this.originalNcaaValue.ncaaProspect);
  }

  get iepNoData() {
    return falsey(this.originalIepValue.iep);
  }

  get fncNoData() {
    return falsey(this.originalFncValue.fnc);
  }

  get historyNoData() {
    return this.historyDetails.length == 0;
  }
}
